import { useState, useEffect, useCallback } from 'react';
import { COMPARISON_OPERATORS } from '@/features/quote-and-apply-builder';

interface UseTableColumnSettingsParams {
  column: PolicyFormTableColumn;
  allColumns: PolicyFormTableColumn[];
  onSave: (column: PolicyFormTableColumn) => void;
  onClose: () => void;
}

export function useTableColumnSettings({
  column,
  allColumns,
  onSave,
  onClose,
}: UseTableColumnSettingsParams) {
  const [id, setId] = useState(column.id);
  const [title, setTitle] = useState(column.title);
  const [type, setType] = useState<PolicyFormTableColumn['type']>(column.type);
  const [required, setRequired] = useState(column.required || false);
  const [placeholder, setPlaceholder] = useState(column.placeholder || '');
  const [validationType, setValidationType] = useState<
    PolicyFormTableColumn['validationType'] | undefined
  >(column.validationType);
  const [options, setOptions] = useState(column.options || []);

  const [hasVisibleCondition, setHasVisibleCondition] = useState(!!column.conditional?.visible);
  const [hasRequiredCondition, setHasRequiredCondition] = useState(!!column.conditional?.required);

  const [visibleConditionType, setVisibleConditionType] = useState<'simple' | 'logical'>(
    column.conditional?.visible && 'conditions' in column.conditional.visible
      ? 'logical'
      : 'simple',
  );
  const [visibleSimpleCondition, setVisibleSimpleCondition] = useState<ColumnFieldCondition>(() => {
    const visible = column.conditional?.visible;
    if (visible && !('conditions' in visible)) {
      return visible as ColumnFieldCondition;
    }
    return { column: '', operator: 'equals', value: '' };
  });
  const [visibleLogicalCondition, setVisibleLogicalCondition] = useState<ColumnLogicalCondition>(
    () => {
      const visible = column.conditional?.visible;
      if (visible && 'conditions' in visible) {
        return visible as ColumnLogicalCondition;
      }
      return { operator: 'and', conditions: [] };
    },
  );

  const [requiredConditionType, setRequiredConditionType] = useState<'simple' | 'logical'>(
    column.conditional?.required && 'conditions' in column.conditional.required
      ? 'logical'
      : 'simple',
  );
  const [requiredSimpleCondition, setRequiredSimpleCondition] = useState<ColumnFieldCondition>(
    () => {
      const req = column.conditional?.required;
      if (req && !('conditions' in req)) {
        return req as ColumnFieldCondition;
      }
      return { column: '', operator: 'equals', value: '' };
    },
  );
  const [requiredLogicalCondition, setRequiredLogicalCondition] = useState<ColumnLogicalCondition>(
    () => {
      const req = column.conditional?.required;
      if (req && 'conditions' in req) {
        return req as ColumnLogicalCondition;
      }
      return { operator: 'and', conditions: [] };
    },
  );

  const [newOptionId, setNewOptionId] = useState('');
  const [newOptionValue, setNewOptionValue] = useState('');

  useEffect(() => {
    setId(column.id);
    setTitle(column.title);
    setType(column.type);
    setRequired(column.required || false);
    setPlaceholder(column.placeholder || '');
    setValidationType(column.validationType);
    setOptions(column.options || []);

    setHasVisibleCondition(!!column.conditional?.visible);
    setHasRequiredCondition(!!column.conditional?.required);

    const visible = column.conditional?.visible;
    if (visible) {
      if ('conditions' in visible) {
        setVisibleConditionType('logical');
        setVisibleLogicalCondition(visible as ColumnLogicalCondition);
      } else {
        setVisibleConditionType('simple');
        setVisibleSimpleCondition(visible as ColumnFieldCondition);
      }
    } else {
      setVisibleConditionType('simple');
      setVisibleSimpleCondition({ column: '', operator: 'equals', value: '' });
      setVisibleLogicalCondition({ operator: 'and', conditions: [] });
    }

    const req = column.conditional?.required;
    if (req) {
      if ('conditions' in req) {
        setRequiredConditionType('logical');
        setRequiredLogicalCondition(req as ColumnLogicalCondition);
      } else {
        setRequiredConditionType('simple');
        setRequiredSimpleCondition(req as ColumnFieldCondition);
      }
    } else {
      setRequiredConditionType('simple');
      setRequiredSimpleCondition({ column: '', operator: 'equals', value: '' });
      setRequiredLogicalCondition({ operator: 'and', conditions: [] });
    }
  }, [column]);

  const hasOptions = ['select', 'radio', 'textselect'].includes(type);

  const otherColumns = allColumns.filter(col => col.id !== column.id);

  const handleSave = useCallback(() => {
    let conditional: ColumnConditionalBehavior | undefined;

    const visibleCond = hasVisibleCondition
      ? visibleConditionType === 'simple' && visibleSimpleCondition.column
        ? visibleSimpleCondition
        : visibleConditionType === 'logical' && visibleLogicalCondition.conditions.length > 0
          ? visibleLogicalCondition
          : undefined
      : undefined;

    const requiredCond = hasRequiredCondition
      ? requiredConditionType === 'simple' && requiredSimpleCondition.column
        ? requiredSimpleCondition
        : requiredConditionType === 'logical' && requiredLogicalCondition.conditions.length > 0
          ? requiredLogicalCondition
          : undefined
      : undefined;

    if (visibleCond || requiredCond) {
      conditional = {};
      if (visibleCond) conditional.visible = visibleCond;
      if (requiredCond) conditional.required = requiredCond;
    }

    const updatedColumn: PolicyFormTableColumn = {
      id,
      title,
      type,
    };

    if (required) updatedColumn.required = true;
    if (placeholder) updatedColumn.placeholder = placeholder;
    if (validationType) updatedColumn.validationType = validationType;
    if (hasOptions && options.length > 0) updatedColumn.options = options;
    if (conditional) updatedColumn.conditional = conditional;

    onSave(updatedColumn);
    onClose();
  }, [
    id,
    title,
    type,
    required,
    placeholder,
    validationType,
    hasOptions,
    options,
    hasVisibleCondition,
    visibleConditionType,
    visibleSimpleCondition,
    visibleLogicalCondition,
    hasRequiredCondition,
    requiredConditionType,
    requiredSimpleCondition,
    requiredLogicalCondition,
    onSave,
    onClose,
  ]);

  const handleAddOption = useCallback(() => {
    if (!newOptionId.trim() || !newOptionValue.trim()) return;
    setOptions(prev => [...prev, { id: newOptionId.trim(), value: newOptionValue.trim() }]);
    setNewOptionId('');
    setNewOptionValue('');
  }, [newOptionId, newOptionValue]);

  const handleUpdateOption = useCallback(
    (
      index: number,
      updates: Partial<{ id: string; value: string; wizardOnly?: boolean; tooltip?: string }>,
    ) => {
      setOptions(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], ...updates };
        return updated;
      });
    },
    [],
  );

  const handleRemoveOption = useCallback((index: number) => {
    setOptions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const getSelectedColumnForCondition = useCallback(
    (fieldCondition: ColumnFieldCondition) => {
      return otherColumns.find(col => col.id === fieldCondition.column);
    },
    [otherColumns],
  );

  const isValueInputHidden = useCallback((operator: string) => {
    return ['empty', 'notEmpty'].includes(operator);
  }, []);

  const handleAddLogicalCondition = useCallback(
    (
      logicalCondition: ColumnLogicalCondition,
      setLogicalCondition: (cond: ColumnLogicalCondition) => void,
    ) => {
      setLogicalCondition({
        ...logicalCondition,
        conditions: [
          ...logicalCondition.conditions,
          { column: '', operator: 'equals', value: '' } as ColumnFieldCondition,
        ],
      });
    },
    [],
  );

  const handleUpdateLogicalCondition = useCallback(
    (
      logicalCondition: ColumnLogicalCondition,
      setLogicalCondition: (cond: ColumnLogicalCondition) => void,
      index: number,
      updates: Partial<ColumnFieldCondition>,
    ) => {
      setLogicalCondition({
        ...logicalCondition,
        conditions: logicalCondition.conditions.map((condition, conditionIndex) =>
          conditionIndex === index ? { ...condition, ...updates } : condition,
        ) as (ColumnFieldCondition | ColumnLogicalCondition)[],
      });
    },
    [],
  );

  const handleRemoveLogicalCondition = useCallback(
    (
      logicalCondition: ColumnLogicalCondition,
      setLogicalCondition: (cond: ColumnLogicalCondition) => void,
      index: number,
    ) => {
      setLogicalCondition({
        ...logicalCondition,
        conditions: logicalCondition.conditions.filter(
          (_, conditionIndex) => conditionIndex !== index,
        ),
      });
    },
    [],
  );

  const isSaveDisabled = !id.trim() || !title.trim();

  return {
    id,
    setId,
    title,
    setTitle,
    type,
    setType,
    required,
    setRequired,
    placeholder,
    setPlaceholder,
    validationType,
    setValidationType,
    options,
    newOptionId,
    setNewOptionId,
    newOptionValue,
    setNewOptionValue,
    hasVisibleCondition,
    setHasVisibleCondition,
    hasRequiredCondition,
    setHasRequiredCondition,
    visibleConditionType,
    setVisibleConditionType,
    visibleSimpleCondition,
    setVisibleSimpleCondition,
    visibleLogicalCondition,
    setVisibleLogicalCondition,
    requiredConditionType,
    setRequiredConditionType,
    requiredSimpleCondition,
    setRequiredSimpleCondition,
    requiredLogicalCondition,
    setRequiredLogicalCondition,
    hasOptions,
    otherColumns,
    isSaveDisabled,
    handleSave,
    handleAddOption,
    handleUpdateOption,
    handleRemoveOption,
    getSelectedColumnForCondition,
    isValueInputHidden,
    handleAddLogicalCondition,
    handleUpdateLogicalCondition,
    handleRemoveLogicalCondition,
    comparisonOperators: COMPARISON_OPERATORS,
  };
}
